import { useState, useEffect } from "react";
import { sanitizeHtml } from "../lib/sanitize";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Login() {
  const { login, login2fa, resend2fa, user, setUser, verifyEmailCode, verifyPhoneCode, resendEmailCode, resendPhoneCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Destination après connexion : URL initialement demandée (ex. lien e-mail /radar?listing=<id>), sinon accueil.
  const from = location.state?.from || sessionStorage.getItem("radar_login_next") || "/";
  useEffect(() => {
    if (location.state?.from) sessionStorage.setItem("radar_login_next", location.state.from);
  }, [location.state]);
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Gating de vérification (compte non vérifié) : e-mail puis SMS.
  const [verify, setVerify] = useState(null); // { emailReq, phoneReq }
  const [emailCode, setEmailCode] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [vLoading, setVLoading] = useState(false);
  // 2FA super-admin (OTP e-mail après mot de passe validé)
  const [twofa, setTwofa] = useState(null); // { challenge, email, sent }
  const [otpCode, setOtpCode] = useState("");

  useEffect(() => {
    if (user) { sessionStorage.removeItem("radar_login_next"); navigate(from, { replace: true }); }
  }, [user, navigate, from]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const p = params.get("sso_error");
    const reason = params.get("reason");
    if (p === "unauthorized") setError(t("login.error.unauthorized"));
    else if (p) setError(reason ? t("login.error.ssoReason", { reason }) : t("login.error.sso"));
    if (p) window.history.replaceState({}, "", "/login");
  }, [setUser]);

  const loginWithMicrosoft = () => {
    const base = encodeURIComponent(window.location.origin);
    window.location.href = `${window.location.origin}/api/auth/microsoft/login?redirect_base=${base}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res && res.requires_verification) {
        setVerify({ emailReq: !!res.email_verification_required, phoneReq: !!res.phone_verification_required });
        return;
      }
      if (res && res.requires_2fa) {
        setTwofa({ challenge: res.challenge, email: res.email, sent: !!res.email_sent });
        return;
      }
      sessionStorage.removeItem("radar_login_next");
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitEmailCode = async () => {
    setError(""); setVLoading(true);
    try {
      const r = await verifyEmailCode(email, emailCode.trim());
      if (r.access_token && !r.phone_verification_required) {
        toast.success("Compte vérifié.");
        sessionStorage.removeItem("radar_login_next");
        navigate(from, { replace: true });
        return;
      }
      if (r.phone_verification_required) {
        setVerify((v) => ({ ...v, emailReq: false, phoneReq: true }));
        toast.success("E-mail vérifié. Saisissez le code SMS reçu.");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setVLoading(false); }
  };

  const submitPhoneCode = async () => {
    setError(""); setVLoading(true);
    try {
      const r = await verifyPhoneCode(email, phoneCode.trim());
      if (r.access_token) {
        toast.success("Compte vérifié.");
        sessionStorage.removeItem("radar_login_next");
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setVLoading(false); }
  };

  const submitOtpCode = async () => {
    setError(""); setVLoading(true);
    try {
      await login2fa(twofa.challenge, otpCode.trim());
      toast.success("Connexion sécurisée validée.");
      sessionStorage.removeItem("radar_login_next");
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setVLoading(false); }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left visual */}
      <div className="relative hidden w-1/2 lg:block">
        <img
          src="https://images.unsplash.com/photo-1730126680640-bb6c3549d775?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt={t("login.villaAlt")}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0F172A]/55" />
        <div className="absolute bottom-0 left-0 p-14 text-white">
          <p className="overline text-[#C6A87C]">{t("login.overline")}</p>
          <h2 className="mt-3 font-display text-5xl leading-tight tracking-tight" dangerouslySetInnerHTML={sanitizeHtml(t("login.heroTitle"))} />
          <p className="mt-4 max-w-md text-slate-300">
            {t("login.heroDesc")}
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="relative flex w-full items-center justify-center bg-white px-6 lg:w-1/2">
        <div className="absolute right-6 top-6"><LanguageSwitcher variant="dark" /></div>
        {twofa ? (
          <div className="w-full max-w-sm animate-fade-up" data-testid="login-2fa-panel">
            <p className="overline text-[#C6A87C]">RADAR</p>
            <h1 className="mt-2 font-display text-3xl tracking-tight text-[#0F172A]">Vérification en deux étapes</h1>
            <p className="mt-2 text-sm text-slate-500">
              Un code de sécurité à 6 chiffres a été envoyé à <span className="font-medium text-[#0F172A]">{twofa.email}</span>. Saisissez-le pour terminer la connexion.
            </p>
            <div className="mt-6">
              <Label className="text-slate-700">Code de sécurité (6 chiffres)</Label>
              <Input data-testid="login-2fa-code" value={otpCode} maxLength={6} inputMode="numeric"
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" className="mt-1.5 h-11" />
              <Button type="button" data-testid="login-2fa-submit" disabled={vLoading || otpCode.trim().length < 6}
                onClick={submitOtpCode} className="mt-4 h-11 w-full rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
                {vLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Valider le code"}
              </Button>
              <button type="button" data-testid="login-2fa-resend"
                onClick={() => resend2fa(twofa.challenge).then(() => toast.success("Nouveau code envoyé.")).catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message))}
                className="mt-3 text-xs font-medium text-[#C6A87C] underline">Renvoyer le code</button>
            </div>
            {error && <p className="mt-4 text-sm text-red-600" data-testid="login-2fa-error">{error}</p>}
            <button type="button" onClick={() => { setTwofa(null); setOtpCode(""); setError(""); }} className="mt-6 text-sm text-slate-500 underline">Retour</button>
          </div>
        ) : verify ? (
          <div className="w-full max-w-sm animate-fade-up" data-testid="login-verify-panel">
            <p className="overline text-[#C6A87C]">RADAR</p>
            <h1 className="mt-2 font-display text-3xl tracking-tight text-[#0F172A]">Vérification requise</h1>
            <p className="mt-2 text-sm text-slate-500">
              Pour votre sécurité, confirmez votre {verify.emailReq ? "adresse e-mail" : "numéro de téléphone"} avant de continuer.
            </p>
            {verify.emailReq ? (
              <div className="mt-6" data-testid="login-verify-email-block">
                <Label className="text-slate-700">Code reçu par e-mail (6 chiffres)</Label>
                <Input data-testid="login-verify-email-code" value={emailCode} maxLength={6}
                  onChange={(e) => setEmailCode(e.target.value)} placeholder="123456" className="mt-1.5 h-11" />
                <Button type="button" data-testid="login-verify-email-submit" disabled={vLoading || emailCode.trim().length < 6}
                  onClick={submitEmailCode} className="mt-4 h-11 w-full rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
                  {vLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Vérifier mon e-mail"}
                </Button>
                <button type="button" data-testid="login-verify-email-resend" onClick={() => resendEmailCode(email).then(() => toast.success("Nouveau code e-mail envoyé.")).catch(() => {})}
                  className="mt-3 text-xs font-medium text-[#C6A87C] underline">Renvoyer le code e-mail</button>
              </div>
            ) : (
              <div className="mt-6" data-testid="login-verify-phone-block">
                <Label className="text-slate-700">Code reçu par SMS (6 chiffres)</Label>
                <Input data-testid="login-verify-phone-code" value={phoneCode} maxLength={6}
                  onChange={(e) => setPhoneCode(e.target.value)} placeholder="123456" className="mt-1.5 h-11" />
                <Button type="button" data-testid="login-verify-phone-submit" disabled={vLoading || phoneCode.trim().length < 6}
                  onClick={submitPhoneCode} className="mt-4 h-11 w-full rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
                  {vLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Vérifier mon téléphone"}
                </Button>
                <button type="button" data-testid="login-verify-phone-resend" onClick={() => resendPhoneCode(email).then(() => toast.success("Nouveau code SMS envoyé.")).catch(() => {})}
                  className="mt-3 text-xs font-medium text-[#C6A87C] underline">Renvoyer le code SMS</button>
              </div>
            )}
            {error && <p className="mt-4 text-sm text-red-600" data-testid="login-verify-error">{error}</p>}
            <button type="button" onClick={() => { setVerify(null); setError(""); }} className="mt-6 text-sm text-slate-500 underline">Retour</button>
          </div>
        ) : (
        <form onSubmit={submit} className="w-full max-w-sm animate-fade-up" data-testid="login-form">
          <p className="overline text-[#C6A87C]">Privateer</p>
          <h1 className="mt-2 font-display text-4xl tracking-tight text-[#0F172A]">{t("login.title")}</h1>
          <p className="mt-2 text-sm text-slate-500">{t("login.subtitle")}</p>

          <div className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700">{t("login.email")}</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">{t("login.password")}</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11"
                required
              />
              <div className="mt-1.5 text-right">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm font-medium text-[#0F172A] underline underline-offset-2 hover:text-[#C6A87C]">
                  {t("login.forgot")}
                </Link>
              </div>
            </div>
          </div>

          {error && (
            <p className="mt-4 text-sm text-red-600" data-testid="login-error">
              {error}
            </p>
          )}

          <Button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="mt-6 h-11 w-full rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("login.submit")}
          </Button>

          <div className="my-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-slate-200" />
            <span className="text-xs text-slate-400">{t("login.or")}</span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>

          <button
            type="button"
            onClick={loginWithMicrosoft}
            data-testid="login-microsoft-btn"
            className="flex h-11 w-full items-center justify-center gap-3 rounded-full border border-slate-300 bg-white text-sm font-medium text-slate-700 transition-colors hover:border-[#0F172A] hover:bg-slate-50"
          >
            <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
              <rect x="1" y="1" width="9" height="9" fill="#F25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
              <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
            </svg>
            {t("login.microsoft")}
          </button>

          <p className="mt-6 text-center text-sm text-slate-500">
            {t("login.noAccount")}{" "}
            <Link to="/register" data-testid="to-register-link" className="font-medium text-[#0F172A] underline">
              {t("login.createAccount")}
            </Link>
          </p>
        </form>
        )}
      </div>
    </div>
  );
}
